import { useActionTypes } from '@/helpers/useActionTypes'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, Modal } from 'react-bootstrap'
import type { Core } from '@form-validation/bundle/full'
import { dueDateIso, DueDateField, todayDateKey } from '@/components/common/DueDateField'
import { Field } from '@/components/common/Field'
import { useAuth } from '@/context/AuthContext'
import { useCrmHub } from '@/context/SignalRContext'
import { createFormValidation, isFormValid } from '@/helpers/formValidationHelper'
import type { CrmUser } from '@/models/api'
import { readError } from '@/services/apiClient'
import { loadAssignableUsers, saveAction } from '@/services/actionService'
import { saveCustomerNote } from '@/services/customerService'

export function CustomerTaskDialog({
  kind,
  customer,
  onClose,
  onSaved,
}: {
  kind: 'note' | 'action'
  customer: { id: string; name: string }
  onClose: () => void
  onSaved?: () => void
}) {
  const title = `Add ${kind} for ${customer.name}`
  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {kind === 'note'
        ? <NoteForm customerId={customer.id} onClose={onClose} onSaved={onSaved} />
        : <ActionForm customerId={customer.id} onClose={onClose} onSaved={onSaved} />}
    </Modal>
  )
}

function NoteForm({ customerId, onClose, onSaved }: { customerId: string; onClose: () => void; onSaved?: () => void }) {
  const { session } = useAuth()
  const { notify } = useCrmHub()
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const fvRef = useRef<Core | null>(null)

  useEffect(() => {
    const form = formRef.current
    if (!form) return
    const fv = createFormValidation(form, {
      fields: { note: { validators: { notEmpty: { message: 'Enter a note' } } } },
    })
    fvRef.current = fv
    return () => { fv.destroy(); fvRef.current = null }
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!await isFormValid(fvRef.current)) return
    setBusy(true)
    setError(null)
    try {
      await saveCustomerNote(customerId, note)
      await notify('SendNoteAdded', { organisationId: customerId, userId: session?.userId, text: note })
      onSaved?.()
      onClose()
    } catch (caught) {
      setError(readError(caught))
      setBusy(false)
    }
  }

  return (
    <Form ref={formRef} noValidate onSubmit={(event) => void submit(event)}>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Field label="Note" name="note" type="textarea" value={note} onChange={setNote} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={busy}>Save note</Button>
      </Modal.Footer>
    </Form>
  )
}

function ActionForm({ customerId, onClose, onSaved }: { customerId: string; onClose: () => void; onSaved?: () => void }) {
  const actionTypes = useActionTypes()
  const { session } = useAuth()
  const { notify } = useCrmHub()
  const [users, setUsers] = useState<CrmUser[]>([])
  const [type, setType] = useState<string>('')
  useEffect(() => { if (!type && actionTypes.length) setType(actionTypes[0]) }, [type, actionTypes])
  const [assignee, setAssignee] = useState(session?.businessUserId ?? '')
  const [due, setDue] = useState(todayDateKey)
  const [detail, setDetail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const fvRef = useRef<Core | null>(null)

  useEffect(() => {
    const form = formRef.current
    if (!form) return
    const fv = createFormValidation(form, {
      fields: {
        due: { validators: { notEmpty: { message: 'Choose a due date' } } },
        assignee: { validators: { notEmpty: { message: 'Choose who this action is for' } } },
        detail: { validators: { notEmpty: { message: 'Enter the action text' } } },
      },
    })
    fvRef.current = fv
    return () => { fv.destroy(); fvRef.current = null }
  }, [])

  useEffect(() => {
    let cancelled = false
    loadAssignableUsers()
      .then((rows) => { if (!cancelled) setUsers(rows) })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!await isFormValid(fvRef.current)) return
    setBusy(true)
    setError(null)
    try {
      const id = await saveAction({
        customerId,
        assignedToId: assignee,
        actionType: type,
        actionDetail: detail,
        actionDate: dueDateIso(due),
      })
      await notify('SendActionAdded', { actionId: id, organisationId: customerId, userId: session?.userId, text: detail })
      onSaved?.()
      onClose()
    } catch (caught) {
      setError(readError(caught))
      setBusy(false)
    }
  }

  return (
    <Form ref={formRef} noValidate onSubmit={(event) => void submit(event)}>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <DueDateField label="Due" value={due} onChange={setDue} />
        <Field label="Action type" name="actionType" options={[...actionTypes]} value={type} onChange={setType} />
        <Form.Group className="mb-3">
          <Form.Label>Assign to</Form.Label>
          <Form.Select name="assignee" value={assignee} onChange={(event) => setAssignee(event.target.value)}>
            <option value="">Select a user</option>
            {users.map((user) => (
              <option key={user.crmAccountUserId} value={user.crmAccountUserId}>{user.fullName || user.emailAddress}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Field label="Action text" name="detail" type="textarea" value={detail} onChange={setDetail} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={busy}>Save action</Button>
      </Modal.Footer>
    </Form>
  )
}


